import React, { useEffect, useRef, useState } from "react";
import {
    Player,
    UIPlayerConfiguration,
} from "theoplayer/THEOplayer.chromeless";
import { trackEvent } from "utils/azureAppI";
import { cx } from "utils/join-classnames";
import {
    Icon,
    Button,
    Spinner,
    VolumeControls,
    ScrubBarV2,
} from "ui/components";
import { VideoState, VideoActionRequest } from "core/models";
import createImageFromVideo from "utils/imageFromVideo";
import { useVideoPlayback } from "ui/context/VideoPlaybackContext";
import Configuration from "Configuration";
import styles from "./VideoPlayer.module.scss";

const VIDEO_FPS = 25;

interface Props {
    type: "stream" | "video";
    videoSrc: string;
    autoplay?: boolean | "end";
    onTimeUpdate?(time: number): void;
    onPlaying?(status: boolean): void;
    overlay?(videoState: VideoState): React.ReactNode;
    controls?: boolean;
    trim?: [number, number];
    actionRequest?: VideoActionRequest;
    useClickOverlay?: boolean;
    poster?: string;
    onScreenshot?(screenshot: string): void;
    onEnded?(): void;
    subtitleTrackSrc?: string;
    onUpdateDuration?(duration: number): void;
    verticalResize?: boolean;
    externalVideoRef?: React.MutableRefObject<Player | null>;
}

const timeDisplay = (
    currentTime: number,
    duration: number,
    type: Props["type"],
) => {
    let timePlayed = new Date(currentTime * 1000).toISOString().substr(11, 8);
    timePlayed =
        timePlayed.substr(0, 2) === "00" ? timePlayed.substr(3) : timePlayed;
    if (type === "stream") {
        return `LIVE: ${timePlayed || "00:00"}`;
    }
    let totalDuration = "00:00";
    if (duration) {
        totalDuration = new Date(duration * 1000).toISOString().substr(11, 8);
    }
    totalDuration =
        totalDuration.substr(0, 2) === "00"
            ? totalDuration.substr(3)
            : totalDuration;
    return `${timePlayed || "00:00"}/ ${totalDuration || "00:00"}`;
};

const VideoPlayerV2: React.FC<Props> = ({
    type,
    videoSrc,
    autoplay: requestedAutoplay = false,
    onTimeUpdate,
    overlay,
    controls = true,
    trim = [0, 0],
    actionRequest,
    onPlaying,
    useClickOverlay = true,
    poster,
    onScreenshot,
    onEnded,
    onUpdateDuration,
    subtitleTrackSrc,
    verticalResize = false,
    externalVideoRef,
}) => {
    const animationFrame = useRef<number | null>(null);
    const localVideoRef = useRef<Player | null>(null);
    const videoRef = externalVideoRef || localVideoRef;
    const playerContainerRef = useRef<HTMLDivElement | null>(null);
    const [hasPlayed, setHasPlayed] = useState<boolean>(false);
    const [playing, setPlaying] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [stalled, setStalled] = useState<boolean>(false);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [volume, setVolume] = useState<number>(1);
    const [volumeMemory, setVolumeMemory] = useState<number>(1);
    const [muted, setMuted] = useState<boolean>(false);
    const [ended, setEnded] = useState<boolean>(false);
    const [size, setSize] = useState<"small" | "medium" | "large">("medium");
    const [volumeSlider, setVolumeSlider] = useState<boolean>(false);
    const [, setError] = useState<Error | null>(null);
    const [videoAutoplay, setVideoAutoplay] = useState<boolean | "end">(
        requestedAutoplay,
    );

    const playingRef = useRef<boolean>(false);
    const [trimStart, trimEnd] = trim;
    const { registerPause } = useVideoPlayback();

    // Controls - Utils
    const getThumbfromFrame = async () => {
        if (!videoRef.current) {
            return;
        }
        const video = videoRef.current.element.querySelector("video");
        console.log("video", video);
        if (video) {
            const blob = await createImageFromVideo(video);
            if (!blob) {
                return;
            }
            const thumbnailURL = URL.createObjectURL(blob);
            if (onScreenshot) {
                onScreenshot(thumbnailURL);
            }
        }
    };

    const enableTextTrack = () => {
        if (videoRef.current && videoRef.current.textTracks) {
            const track = videoRef.current.textTracks[0];
            if (track) {
                track.mode = "showing";
            }
        }
    };

    const forceStopVideo = (): void => {
        handlePause();
        setVideoAutoplay(false);
        setEnded(true);
    };

    const animate = () => {
        if (
            !videoRef.current ||
            !playingRef.current ||
            trimStart === undefined ||
            trimEnd === undefined
        ) {
            return;
        }
        const calculatedDuration = duration - (trimStart + trimEnd);
        const calculatedTime = videoRef.current.currentTime - trimStart;

        if (videoRef.current.currentTime < trimStart) {
            videoRef.current.currentTime = trimStart;
        } else if (calculatedTime === calculatedDuration) {
            forceStopVideo();
        } else if (calculatedTime > calculatedDuration) {
            forceStopVideo();
            videoRef.current.currentTime = duration - trimEnd;
        }
        animationFrame.current = requestAnimationFrame(animate);
    };

    const THEO_LICENSE_KEY = Configuration.get("theoLicenseKey");

    // Player
    useEffect(() => {
        // Create player inside container.
        if (playerContainerRef.current) {
            console.log("THEOplayerView, creating THEOplayer");
            const config: UIPlayerConfiguration = {
                license: THEO_LICENSE_KEY,
                mutedAutoplay: "all",
                allowNativeFullscreen: true,
            };
            videoRef.current = new Player(playerContainerRef.current, config);
        }

        // Clean up
        return () => {
            console.log("THEOplayerView, destroying THEOplayer");
            if (videoRef.current) {
                videoRef.current.destroy();
            }
        };
    }, [playerContainerRef]);

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        videoRef.current.muted = muted;
        videoRef.current.autoplay = videoAutoplay as boolean;
        videoRef.current.source = {
            sources: [
                {
                    src: videoSrc,
                    crossOrigin: "anonymous",
                },
            ],
            textTracks: [
                {
                    src: subtitleTrackSrc || "",
                },
            ],
        };
        videoRef.current.poster = poster || "";
    }, [videoRef.current, subtitleTrackSrc]);

    // Controls - Handlers
    const handleVideoEvent = (e: any) => {
        if (!videoRef.current) {
            return;
        }
        switch (e.type) {
            case "playing":
                setPlaying(true);
                playingRef.current = true;
                setEnded(false);
                setStalled(false);
                setHasPlayed(true);
                break;
            case "paused":
                setPlaying(false);
                playingRef.current = false;
                break;
            case "canplay": // Fires when the browser can start playing the audio/video
                setLoading(false);
                setStalled(false);
                if (videoAutoplay === "end" && !playing) {
                    videoRef.current.currentTime = duration - 1 || 0;
                    handlePlay();
                }
                break;
            case "canplaythrough": // Fires when the browser can play through the audio/video without stopping for buffering
                setLoading(false);
                setStalled(false);
                if (requestedAutoplay && !hasPlayed) {
                    handlePlay();
                }
                break;
            case "progress": // Fires when the browser is downloading the audio/video
                break;
            case "loadstart": // Fires when the browser starts looking for the audio/video
                setVideoAutoplay(requestedAutoplay);
                setLoading(true);
                break;
            case "seeking": // Fires when the user starts moving/skipping to a new position in the audio/video
                setEnded(false);
                break;
            case "seeked": // Fires when the user is finished moving/skipping to a new position in the audio/video
                setEnded(false);
                break;
            case "waiting": // Fires when the browser is trying to get media data, but data is not available
                break;
            case "timeupdate": // Fires when the current playback position has changed
                setCurrentTime(videoRef.current.currentTime);
                if (onTimeUpdate) {
                    onTimeUpdate(videoRef.current.currentTime);
                }
                break;
            case "ended": // Returns whether the playback of the audio/video has ended or not
                setEnded(true);
                setPlaying(false);
                if (onPlaying) {
                    onPlaying(false);
                }
                if (onEnded) {
                    onEnded();
                }
                break;
            case "loadeddata": // Fires when the browser has loaded the current frame of the audio/video
                break;
            case "loadedmetadata": // Fires when the metadata is loaded
                setDuration(videoRef.current.duration);
                if (onUpdateDuration) {
                    onUpdateDuration(videoRef.current.duration);
                }
                enableTextTrack();
                break;
            default:
                // throw Error("Default case VideoEvent", e);
                break;
        }
    };

    const togglePlay = async (): Promise<void> => {
        console.log("togglePlay called");
        console.trace();
        if (videoRef.current) {
            if (!playing) {
                setPlaying(true);
                videoRef.current.play();
            } else {
                videoRef.current.pause();
                setPlaying(false);
                if (onScreenshot) {
                    await getThumbfromFrame();
                }
            }
        }
    };

    const handleSetTime = (newTime: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = newTime;
        }
    };

    const handlePlay = () => {
        console.log("handlePlay called");
        console.trace();
        setPlaying(true);
        if (videoRef.current) {
            videoRef.current.play();
        }
    };

    const handlePause = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
        setPlaying(false);
    };

    const handleVolumeChange = (newValue: number) => {
        if (videoRef.current) {
            videoRef.current.volume = newValue;
        }
        setVolume(newValue);
        setMuted(false);
    };

    const handleMuteChange = (newStatus: boolean) => {
        if (newStatus) {
            setVolumeMemory(volume);
        }
        if (videoRef.current) {
            videoRef.current.volume = newStatus ? 0 : volumeMemory;
        }
        setMuted(newStatus);
        setVolume(newStatus ? 0 : volumeMemory);
    };

    const handleVolumeSliderToggle = (state: boolean) => {
        setVolumeSlider(state);
    };

    const handleFullscreen = () => {
        if (!document.fullscreenElement && playerContainerRef.current) {
            const videoContainer = playerContainerRef.current.parentElement;
            if (videoContainer) {
                videoContainer.requestFullscreen();
                trackEvent({
                    name: "FullScreenEnabled",
                });
            }
        } else if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    };

    // Controls - Events
    useEffect(() => {
        if (videoRef.current) {
            // Set up events
            videoRef.current.addEventListener(
                ["playing", "play"],
                handleVideoEvent,
            );
            videoRef.current.addEventListener("pause", handleVideoEvent);
            videoRef.current.addEventListener("canplay", handleVideoEvent);
            videoRef.current.addEventListener(
                "canplaythrough",
                handleVideoEvent,
            );
            videoRef.current.addEventListener("progress", handleVideoEvent);
            videoRef.current.addEventListener("loadstart", handleVideoEvent);
            videoRef.current.addEventListener("seeking", handleVideoEvent);
            videoRef.current.addEventListener("seeked", handleVideoEvent);
            videoRef.current.addEventListener("waiting", handleVideoEvent);
            videoRef.current.addEventListener("timeupdate", handleVideoEvent);
            videoRef.current.addEventListener("ended", handleVideoEvent);
            videoRef.current.addEventListener("loadeddata", handleVideoEvent);
            videoRef.current.addEventListener(
                "loadedmetadata",
                handleVideoEvent,
            );
        }

        return () => {
            if (!videoRef.current) {
                return;
            }
            // Clear events
            videoRef.current.removeEventListener(
                ["playing", "play"],
                handleVideoEvent,
            );
            videoRef.current.removeEventListener("pause", handleVideoEvent);
            videoRef.current.removeEventListener("canplay", handleVideoEvent);
            videoRef.current.removeEventListener(
                "canplaythrough",
                handleVideoEvent,
            );
            videoRef.current.removeEventListener("progress", handleVideoEvent);
            videoRef.current.removeEventListener("loadstart", handleVideoEvent);
            videoRef.current.removeEventListener("seeking", handleVideoEvent);
            videoRef.current.removeEventListener("seeked", handleVideoEvent);
            videoRef.current.removeEventListener("waiting", handleVideoEvent);
            videoRef.current.removeEventListener(
                "timeupdate",
                handleVideoEvent,
            );
            videoRef.current.removeEventListener("ended", handleVideoEvent);
            videoRef.current.removeEventListener(
                "loadeddata",
                handleVideoEvent,
            );
            videoRef.current.removeEventListener(
                "loadedmetadata",
                handleVideoEvent,
            );
        };
    }, [videoRef.current]);

    const handleReplay = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
        }
        setEnded(false);
    };

    useEffect(() => {
        const handleActionRequest = async () => {
            if (!actionRequest) {
                return;
            }
            if (!videoRef.current || actionRequest.type === "none") {
                return;
            }
            console.log(
                `Action request received: ${actionRequest.type} with value: ${actionRequest.value}`,
            );
            switch (actionRequest.type) {
                case "togglePlay":
                    await togglePlay();
                    break;
                case "play":
                    handlePlay();
                    break;
                case "pause":
                    handlePause();
                    if (onScreenshot) {
                        await getThumbfromFrame();
                    }
                    break;
                case "rewind":
                    videoRef.current.currentTime = 0 + trimStart;
                    break;
                case "setTime":
                    videoRef.current.currentTime = actionRequest.value || 0;
                    break;
                case "setTimePaused":
                    handlePause();
                    videoRef.current.currentTime = actionRequest.value || 0;
                    break;
                case "frameForwards":
                    setVideoAutoplay(false);
                    videoRef.current.currentTime += 1 / VIDEO_FPS;
                    handlePause();
                    break;
                case "frameBackwards":
                    setVideoAutoplay(false);
                    videoRef.current.currentTime -= 1 / VIDEO_FPS;
                    handlePause();
                    break;
                case "playFromStart":
                    videoRef.current.currentTime = 0;
                    break;
                case "playLastSeconds":
                    videoRef.current.currentTime =
                        (duration || 0) - trimEnd - (actionRequest.value || 0);
                    break;
                case "getScreenshot":
                    handlePause();
                    await getThumbfromFrame();
                    break;
                default:
                    throw Error("Default case actionRequest");
            }
        };

        handleActionRequest();
    }, [actionRequest]);

    useEffect(() => {
        const video = videoRef.current;

        if (video && video.videoWidth < 400) {
            setSize("small");
        } else if (video && video.videoWidth > 800) {
            setSize("large");
        } else {
            setSize("medium");
        }
    }, [videoRef.current]);

    useEffect(() => {
        if (!onPlaying) {
            return;
        }
        onPlaying(playing);
    }, [playing, onPlaying]);

    useEffect(() => {
        if (requestedAutoplay === videoAutoplay) {
            return;
        }
        setVideoAutoplay(requestedAutoplay);
    }, [requestedAutoplay]);

    const resetAnimationframe = (): void => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
        }
    };
    useEffect(() => {
        if (type === "stream") {
            return;
        }
        if (playing) {
            animationFrame.current = requestAnimationFrame(animate);
        }
        playingRef.current = playing;
        return () => resetAnimationframe();
    }, [playing]);

    useEffect(() => registerPause(handlePause), []);

    return (
        <div
            className={cx(
                styles.video__container,
                size === "large" ? styles.large : null,
                size === "small" ? styles.small : null,
                verticalResize ? styles["video__container--vertical"] : null,
            )}
        >
            {overlay && (
                <div className={styles.video__overlay__custom}>
                    {overlay({
                        playing,
                        buffering: loading,
                        duration: duration - (trimStart + trimEnd),
                        currentTime: (currentTime || 0) - trimStart,
                        setCurrentTime: handleSetTime,
                        play: handlePlay,
                        pause: handlePause,
                        vidRef: videoRef,
                    })}
                </div>
            )}
            {controls && (
                <div className={styles.video__overlay__default}>
                    <div
                        className={styles.clickoverlay}
                        role="presentation"
                        onClick={useClickOverlay ? togglePlay : undefined}
                    >
                        {!playing && !loading && !stalled && (
                            <div
                                className={styles.btn__play__main}
                                role="presentation"
                                onClick={togglePlay}
                            >
                                <Icon name="play" />
                            </div>
                        )}
                        {ended && !stalled && (
                            <div
                                className={styles.btn__play__main}
                                role="presentation"
                                onClick={handleReplay}
                            >
                                <Icon name="reload" />
                            </div>
                        )}
                    </div>

                    {type !== "stream" && (
                        <div className={styles.progressbar}>
                            <ScrubBarV2 vidRef={videoRef.current} size={size} />
                        </div>
                    )}

                    <div className={styles.buttonbar}>
                        <Button
                            type="function"
                            icon={playing ? "pause" : "play"}
                            onClick={togglePlay}
                            size={size !== "small" ? "large" : "medium"}
                        />
                        <div className={styles.volume__controls}>
                            <VolumeControls
                                volumeValue={volume}
                                muted={muted}
                                onChange={handleVolumeChange}
                                onMuteChange={handleMuteChange}
                                size={size}
                                onSliderToggle={handleVolumeSliderToggle}
                            />
                        </div>
                        {volumeSlider && size === "small" ? null : (
                            <div className={styles.timedisplay}>
                                {timeDisplay(currentTime, duration, type)}
                            </div>
                        )}
                        <div className={styles.spacer} />
                        {document.fullscreenEnabled && (
                            <Button
                                type="function"
                                icon="fullscreen"
                                size={size !== "small" ? "large" : "medium"}
                                onClick={handleFullscreen}
                            />
                        )}
                    </div>
                </div>
            )}
            {loading && !overlay && (
                <div className={styles.video__loading}>
                    <span className={styles.video__loading__text}>
                        <Spinner size={size === "small" ? "small" : "medium"} />
                    </span>
                </div>
            )}

            <div ref={playerContainerRef} className={styles.video__player} />
        </div>
    );
};

export default VideoPlayerV2;
